import React from 'react'
import {connect, useSelector} from 'react-redux'
import {bindActionCreators} from 'redux'
import {applySpec, isNil, all} from 'ramda'
import {PRESENTER_TYPES} from 'modules/common/constants'
import {
  getErrorByPresenterType,
  didSoldOutErrorHappen
} from 'modules/common/selectors'
import {
  refreshPage,
  historyBack,
  navigateToRoomSelection,
  navigateToSearch
} from 'modules/browser/actions'

import {selectIsAuthenticated} from '@daedalus/core/src/auth/modules/selectors'
import {useIsAudience} from '@daedalus/core/src/auth/modules/useCognito'

import withURLParamsMapper from 'components/data/URLParams/withURLParamsMapper'
import ErrorPresenter from 'components/errorMonitors/ErrorPresenter'
import overlayConfigurationFactory from './overlayConfigurationFactory'
import {CommonErrorPayloadFormatType} from 'types/errorResponse'
import {FETCH_BOOKING_ERROR} from 'modules/book/actions/fetchBooking'
import {getBookingModuleStatus} from 'modules/book/selectors'
import {BOOK_OFFER_FINALIZATION_ERROR} from 'modules/book/actions/bookOfferFinalization'
import {bookOfferCreationError} from 'modules/book/slice'

type ErrorOverlayMonitorType = {
  genericError: CommonErrorPayloadFormatType
  historyBack: () => void
  navigateToRoomSelection: () => void
  navigateToSearch: (isAuthenticated?: boolean, isAudience?: boolean) => void
  refreshPage: () => void
  unknownError: CommonErrorPayloadFormatType
}

const BOOKING_ERRORS = new Set([
  FETCH_BOOKING_ERROR,
  BOOK_OFFER_FINALIZATION_ERROR,
  bookOfferCreationError.type
])

export const ErrorOverlayMonitor = ({
  unknownError,
  genericError,
  historyBack,
  refreshPage,
  navigateToSearch,
  navigateToRoomSelection
}: ErrorOverlayMonitorType) => {
  const isAuthenticated = useSelector(selectIsAuthenticated)
  const isAudience = useIsAudience()
  const isSoldOut = useSelector(didSoldOutErrorHappen)
  const bookingModuleStatus = useSelector(getBookingModuleStatus)

  if (BOOKING_ERRORS.has(bookingModuleStatus)) return null

  const handleBackToSearch = () => {
    navigateToSearch({isAuthenticated, isAudience})
  }

  const handleBackToRoomSelection = () => {
    navigateToRoomSelection()
  }

  if (all(isNil, [unknownError, genericError]) || isSoldOut) {
    return null
  }

  const overlayActions = {
    refreshPage,
    historyBack,
    backToSearch: handleBackToSearch,
    backToRoomSelection: handleBackToRoomSelection
  }

  const overlayConfiguration = overlayConfigurationFactory(
    unknownError || genericError,
    overlayActions
  )

  return <ErrorPresenter {...overlayConfiguration} />
}

const mapDispatchToProps = (dispatch: any) =>
  bindActionCreators(
    {
      historyBack,
      refreshPage,
      navigateToRoomSelection,
      navigateToSearch
    },
    dispatch
  )

const mapStateToProps = state => {
  return applySpec({
    unknownError: getErrorByPresenterType(PRESENTER_TYPES.UnknownError),
    genericError: getErrorByPresenterType(PRESENTER_TYPES.GenericError)
  })(state)
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withURLParamsMapper(React.memo<ErrorOverlayMonitorType>(ErrorOverlayMonitor)))
